import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Play } from "lucide-react";
import { useStudio, useLookups } from "@/store/StudioContext";
import MediaFrame from "@/components/common/MediaFrame";
import DeleteAssetDialog from "@/components/common/DeleteAssetDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { StatusPill } from "@/components/common/States";
import { t } from "@/i18n";

const Group = ({ title, id, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#262626]">
      <button data-testid={`library-group-${id}`} onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-2.5 text-left">
        <span className="v-eyebrow">{title}</span><span className="text-xs text-[#6b6b6b]">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

const LibraryPanel = ({ data }) => {
  const { state, dispatch } = useStudio();
  const { assetById } = useLookups();
  const navigate = useNavigate();
  const { project, scenes, assets, selection, references: projectReferences } = data;
  const visibleReferences = projectReferences || (state.references || []).filter((r) => r.projectId === project?.id);
  const [toDelete, setToDelete] = useState(null); // { assetId, sceneId }
  const [pendingReplaceVersion, setPendingReplaceVersion] = useState(null); // asset

  const selectedScene = scenes.find((s) => s.id === selection.sceneId) || scenes[0] || null;
  const targetSceneIndex = selectedScene ? scenes.findIndex((s) => s.id === selectedScene.id) : -1;
  const targetSceneTitle = selectedScene ? (selectedScene.title || t.storyboard.sceneN(targetSceneIndex + 1)) : "";

  const versions = [...assets].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  const sceneOf = (a) => a.sceneId || scenes.find((s) => s.assetIds?.includes(a.id) || s.selectedAssetId === a.id)?.id || null;

  const applyAssignAsset = (sceneId, assetId) => {
    dispatch({
      type: "SCENE_SELECT_ASSET",
      payload: { sceneId, assetId },
    });
    dispatch({
      type: "SELECT",
      payload: {
        projectId: project.id,
        patch: { sceneId, assetId },
      },
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto" data-testid="library-panel-body">
      <div className="flex h-11 shrink-0 items-center border-b border-[#303030] px-4 text-sm font-semibold">{t.project.library}</div>

      <Group title={`${t.library.scenes} (${scenes.length})`} id="scenes">
        {scenes.length === 0 ? <p className="px-1 text-xs text-[#a3a3a3]">{t.library.empty}</p> : (
          <ul className="flex flex-col gap-1">
            {scenes.map((s, i) => (
              <li key={s.id}>
                <button
                  data-testid={`library-scene-${s.id}`}
                  onClick={() => {
                    const activeAssetId = (s.selectedAssetId && assetById[s.selectedAssetId] && assetById[s.selectedAssetId].projectId === project.id)
                      ? s.selectedAssetId
                      : (s.assetIds || []).find((id) => assetById[id] && assetById[id].projectId === project.id) || null;
                    dispatch({
                      type: "SELECT",
                      payload: {
                        projectId: project.id,
                        patch: { sceneId: s.id, assetId: activeAssetId },
                      },
                    });
                  }}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${selection.sceneId === s.id ? "bg-[#222] text-[#f4f1ec]" : "text-[#a3a3a3] hover:bg-[#1b1b1b]"}`}
                >
                  <MediaFrame asset={assetById[s.selectedAssetId]} className="h-7 w-11 shrink-0 rounded-sm" showBadge={false} />
                  <span className="min-w-0 flex-1 truncate"><span className="font-mono text-[10px] text-[#6b6b6b]">{i + 1}</span> {s.title || t.common.untitled}</span>
                  <StatusPill status={s.status} map={t.storyboard.status} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Group>

      <Group title={`${t.library.references} (${visibleReferences.length})`} id="references">
        <button
          type="button"
          data-testid="library-manage-resources-btn"
          onClick={() => navigate(`/projects/${project.id}/resources`)}
          className="mb-2 flex w-full items-center justify-between rounded border border-[#303030] bg-[#1a1a1a] px-2.5 py-1.5 text-xs text-[#a3a3a3] hover:border-[#f5b544]/60 hover:text-[#f5b544] transition-colors"
        >
          <span className="font-medium text-[#d8d4cd]">{t.project.projectResources || "Recursos del proyecto"}</span>
          <span className="text-[11px] text-[#f5b544]">Ver todos →</span>
        </button>
        {visibleReferences.length === 0 ? <p className="px-1 text-xs text-[#a3a3a3]">{t.library.empty}</p> : (
          <div className="grid grid-cols-3 gap-2">
            {visibleReferences.map((r) => {
              const inScene = selectedScene?.referenceIds.includes(r.id);
              return (
                <button key={r.id} data-testid={`library-ref-${r.id}`} disabled={!selectedScene} title={selectedScene ? (inScene ? t.library.removeReference : t.library.useAsReference) : t.project.noSelection} onClick={() => dispatch({ type: "SCENE_TOGGLE_REFERENCE", payload: { sceneId: selectedScene.id, referenceId: r.id } })} className="group text-left disabled:opacity-50">
                  <MediaFrame asset={assetById[r.assetId]} fit="contain" className={`aspect-square transition-[border-color] bg-[#121212] ${inScene ? "border-[#f5b544]" : "group-hover:border-[#4a4a4a]"}`} showBadge={false} />
                  <div className={`mt-1 truncate font-mono text-[10px] ${inScene ? "text-[#f5b544]" : "text-[#a3a3a3]"}`}>{r.label}</div>
                </button>
              );
            })}
          </div>
        )}
      </Group>

      <Group title={`${t.library.versions} (${versions.length})`} id="versions">
        {versions.length === 0 ? <p className="px-1 text-xs text-[#a3a3a3]">{t.library.empty}</p> : (
          <div className="grid grid-cols-2 gap-2">
            {versions.map((a) => {
              const isSelected = selection.assetId === a.id;
              const isAssignedToSelectedScene = Boolean(selectedScene && selectedScene.selectedAssetId === a.id);
              return (
                <div key={a.id} className="group relative flex flex-col justify-between rounded border border-[#262626] bg-[#161616] p-1.5">
                  <div className="relative">
                    <button
                      data-testid={`library-version-${a.id}`}
                      title={t.library.openEditor}
                      onClick={() => {
                        const targetSceneId = sceneOf(a) || selection.sceneId || null;
                        dispatch({ type: "SELECT", payload: { projectId: project.id, patch: { assetId: a.id, sceneId: targetSceneId } } });
                        if (targetSceneId && sceneOf(a)) {
                          dispatch({ type: "SCENE_SELECT_ASSET", payload: { sceneId: targetSceneId, assetId: a.id } });
                        }
                        navigate(`/projects/${project.id}/editor`);
                      }}
                      className="block w-full text-left"
                    >
                      <MediaFrame asset={a} fit="contain" className={`aspect-video transition-[border-color] bg-[#121212] ${isSelected ? "border-[#f5b544]" : "group-hover:border-[#4a4a4a]"}`} />
                      {a.type === "video" && <span className="pointer-events-none absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded bg-[#101010]/80 px-1 py-0.5 text-[9px] text-[#f4f1ec]"><Play size={9} />Vídeo</span>}
                      <div className="mt-1 truncate text-[10px] text-[#a3a3a3]">{a.label}</div>
                    </button>
                    <button
                      type="button"
                      data-testid={`library-version-delete-${a.id}`}
                      title="Quitar o eliminar"
                      aria-label="Quitar o eliminar"
                      onClick={() => setToDelete({ assetId: a.id, sceneId: sceneOf(a) })}
                      className="absolute right-1.5 top-1.5 rounded bg-[#101010]/85 p-1 text-[#d8d4cd] opacity-70 transition-opacity hover:bg-red-500/30 hover:text-red-300 group-hover:opacity-100 focus-visible:opacity-100"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>

                  {selectedScene && (
                    isAssignedToSelectedScene ? (
                      <div
                        data-testid={`library-assigned-badge-${a.id}`}
                        className="mt-1.5 flex items-center justify-center gap-1 rounded border border-[#f5b544]/40 bg-[#f5b544]/10 py-1 px-1.5 text-[10px] font-medium text-[#f5b544]"
                      >
                        <span>✓ {t.library.activeInScene || "Activo en escena"}</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        data-testid={`library-assign-to-scene-${a.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedScene.selectedAssetId && selectedScene.selectedAssetId !== a.id) {
                            setPendingReplaceVersion(a);
                          } else {
                            applyAssignAsset(selectedScene.id, a.id);
                          }
                        }}
                        className="mt-1.5 flex w-full items-center justify-center gap-1 rounded border border-[#383838] bg-[#222] py-1 px-1.5 text-[10px] font-medium text-[#d8d4cd] hover:border-[#f5b544]/60 hover:bg-[#2a2415] hover:text-[#f5b544] transition-colors"
                        title={
                          typeof t.library.assignToSceneNamed === "function"
                            ? t.library.assignToSceneNamed(targetSceneTitle)
                            : t.library.assignToScene
                        }
                      >
                        <span className="truncate">
                          {typeof t.library.assignToSceneNamed === "function"
                            ? t.library.assignToSceneNamed(targetSceneTitle)
                            : t.library.assignToScene}
                        </span>
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Group>

      <DeleteAssetDialog
        open={Boolean(toDelete)}
        assetIds={toDelete ? [toDelete.assetId] : []}
        sceneId={toDelete?.sceneId || null}
        onClose={() => setToDelete(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingReplaceVersion)}
        onOpenChange={(open) => !open && setPendingReplaceVersion(null)}
        title={
          typeof t.storyboard.replaceMediaTitle === "function"
            ? t.storyboard.replaceMediaTitle(targetSceneTitle)
            : t.storyboard.replaceMediaTitle
        }
        body={t.storyboard.replaceMediaBody}
        confirmLabel={t.storyboard.replaceMediaConfirm}
        destructive={false}
        onConfirm={() => {
          if (pendingReplaceVersion && selectedScene) {
            applyAssignAsset(selectedScene.id, pendingReplaceVersion.id);
            setPendingReplaceVersion(null);
          }
        }}
      />
    </div>
  );
};

export default LibraryPanel;

